import { useCallback, useMemo, useState } from 'react';
import {
  HingeSelectClient,
  manualSourceTracking,
  useHingeSelectClient,
} from '../../api';
import {
  ContractState,
  CorpType,
  FeeScheduleState,
  OrganizationState,
  ServiceProviderType,
  ServiceType,
} from '../../api/types';
import { CommitLogEntry, WizardState } from '../context/types';
import { useWizardState } from '../context/wizard-state.context';
import { addressInputToProto } from '../utils/address-input-to-proto';
import { dollarsToMoney } from '../utils/dollars-to-money';
import {
  accessPointKey as accessPointKeyRaw,
  contractKey as contractKeyRaw,
  contractStateKey as contractStateKeyRaw,
  cspBulkKey as cspBulkKeyRaw,
  feeScheduleKey as feeScheduleKeyRaw,
  feeScheduleStateKey as feeScheduleStateKeyRaw,
  linkFeeScheduleToContractKey as linkFeeScheduleToContractKeyRaw,
  linkMerchantLocationKey as linkMerchantLocationKeyRaw,
  merchantKey as merchantKeyRaw,
  merchantLocationKey as merchantLocationKeyRaw,
  organizationKey as organizationKeyRaw,
  providerAccountKey as providerAccountKeyRaw,
  providerIndividualKey as providerIndividualKeyRaw,
} from '../utils/idempotency-keys';
import { isoToProtoDate } from '../utils/iso-to-protodate';
import { normalizeFein } from '../utils/normalize-fein';
import { getOrgGroups } from '../utils/org-groups';
import { phoneToE164 } from '../utils/phone-to-e164';

/**
 * Per-run scratchpad for ids returned by create-steps. Subsequent steps that
 * need those ids (e.g. linkMerchantLocationToMerchant) read from here rather
 * than from `state.resolutions`, since React state isn't re-rendered mid-run
 * and pre-commit resolutions don't include freshly-minted ids.
 *
 * Multi-org runs produce one Contract per distinct TIN; the per-TIN records
 * below are keyed by tax_id. Single-org runs use a one-entry record — the
 * indexing is uniform regardless of TIN count.
 */
export interface RuntimeRegistry {
  merchantIds: Record<string, string>;
  merchantLocationIds: Record<string, string>;
  organizationIds: Record<string, string>;
  providerAccountIds: Record<string, string>;
  contractIds: Record<string, string>;
  feeScheduleId?: string;
}

// Resume rebuilds runtime from scratch, but the IDs from successful create
// steps were already captured verbatim into commit.log — read them back so a
// Resume past createFeeSchedule / createContract / etc. doesn't send empty
// IDs to dependent link calls. Step names come un-suffixed (single-org) or
// TIN-suffixed (multi-org); the fallback TIN for un-suffixed names is
// input.locations[0].tax_id, valid because a single-org log carries one TIN.
const seedRuntimeFromLog = (
  log: CommitLogEntry[],
  input: WizardState['input'],
): {
  merchantIds: Record<string, string>;
  organizationIds: Record<string, string>;
  providerAccountIds: Record<string, string>;
  contractIds: Record<string, string>;
  feeScheduleId?: string;
  merchantLocationIds: Record<string, string>;
} => {
  const fallbackTin = input?.locations[0]?.tax_id ?? '';
  const seed: {
    merchantIds: Record<string, string>;
    organizationIds: Record<string, string>;
    providerAccountIds: Record<string, string>;
    contractIds: Record<string, string>;
    feeScheduleId?: string;
    merchantLocationIds: Record<string, string>;
  } = {
    merchantIds: {},
    organizationIds: {},
    providerAccountIds: {},
    contractIds: {},
    merchantLocationIds: {},
  };
  for (const e of log) {
    if (e.status !== 'success' || e.response == null) continue;
    const r = e.response as Record<string, unknown>;
    if (e.step === 'createMerchant' || e.step.startsWith('createMerchant:')) {
      const tin = e.step.startsWith('createMerchant:')
        ? e.step.slice('createMerchant:'.length)
        : fallbackTin;
      const id = (r.merchant as { id?: string })?.id;
      if (id) seed.merchantIds[tin] = id;
    } else if (
      e.step === 'ensureOrganization' ||
      e.step.startsWith('ensureOrganization:')
    ) {
      const tin = e.step.startsWith('ensureOrganization:')
        ? e.step.slice('ensureOrganization:'.length)
        : fallbackTin;
      const id = (r.organization as { organizationId?: string })
        ?.organizationId;
      if (id) seed.organizationIds[tin] = id;
    } else if (
      e.step === 'createProviderAccount' ||
      e.step.startsWith('createProviderAccount:')
    ) {
      const tin = e.step.startsWith('createProviderAccount:')
        ? e.step.slice('createProviderAccount:'.length)
        : fallbackTin;
      const id = (r.providerAccount as { providerAccountId?: string })
        ?.providerAccountId;
      if (id) seed.providerAccountIds[tin] = id;
    } else if (
      e.step === 'createContract' ||
      e.step.startsWith('createContract:')
    ) {
      const tin = e.step.startsWith('createContract:')
        ? e.step.slice('createContract:'.length)
        : fallbackTin;
      const id = (r.contract as { id?: string })?.id;
      if (id) seed.contractIds[tin] = id;
    } else if (e.step === 'createFeeSchedule') {
      seed.feeScheduleId =
        (r.feeSchedule as { id?: string })?.id ?? seed.feeScheduleId;
    } else if (e.step.startsWith('createMerchantLocation:')) {
      const key = e.step.slice('createMerchantLocation:'.length);
      const id = (r.merchantLocation as { id?: string })?.id;
      if (id) seed.merchantLocationIds[key] = id;
    }
  }
  return seed;
};

export interface PlannedAction {
  step: string;
  description: string;
  idempotencyKey: string;
  /** When false, this row is "Reuse" — not a write. */
  isWrite: boolean;
  execute?: (
    client: HingeSelectClient,
    state: WizardState,
    runtime: RuntimeRegistry,
  ) => Promise<unknown>;
}

// Exported for unit testing the plan shape without the React hook + BFF client.
export const buildPlan = (
  state: WizardState,
  resumeAttempt: number,
  resumeNonce: string,
): PlannedAction[] => {
  const plan: PlannedAction[] = [];
  const input = state.input;
  if (input == null) return plan;
  const groups = getOrgGroups(input);
  const isMultiOrg = groups.length > 1;

  // Suffix idempotency keys with the resume-attempt counter plus a per-retry
  // random nonce so each user-driven retry presents a fresh key to the BFF/KG
  // service — the attempt number alone could theoretically repeat across
  // sessions, but `${attempt}-${nonce}` cannot. Attempt 0 is unsuffixed so the
  // first commit looks identical to the pre-retry-counter behavior.
  const k = (key: string): string =>
    resumeAttempt > 0 ? `${key}:r${resumeAttempt}-${resumeNonce}` : key;
  const accessPointKey: typeof accessPointKeyRaw = (...a) =>
    k(accessPointKeyRaw(...a));
  const contractKey: typeof contractKeyRaw = (...a) => k(contractKeyRaw(...a));
  const contractStateKey: typeof contractStateKeyRaw = (...a) =>
    k(contractStateKeyRaw(...a));
  const cspBulkKey: typeof cspBulkKeyRaw = (...a) => k(cspBulkKeyRaw(...a));
  const feeScheduleKey: typeof feeScheduleKeyRaw = (...a) =>
    k(feeScheduleKeyRaw(...a));
  const feeScheduleStateKey: typeof feeScheduleStateKeyRaw = (...a) =>
    k(feeScheduleStateKeyRaw(...a));
  const linkFeeScheduleToContractKey: typeof linkFeeScheduleToContractKeyRaw = (
    ...a
  ) => k(linkFeeScheduleToContractKeyRaw(...a));
  const linkMerchantLocationKey: typeof linkMerchantLocationKeyRaw = (...a) =>
    k(linkMerchantLocationKeyRaw(...a));
  const merchantKey: typeof merchantKeyRaw = (...a) => k(merchantKeyRaw(...a));
  const merchantLocationKey: typeof merchantLocationKeyRaw = (...a) =>
    k(merchantLocationKeyRaw(...a));
  const organizationKey: typeof organizationKeyRaw = (...a) =>
    k(organizationKeyRaw(...a));
  const providerAccountKey: typeof providerAccountKeyRaw = (...a) =>
    k(providerAccountKeyRaw(...a));
  const providerIndividualKey: typeof providerIndividualKeyRaw = (...a) =>
    k(providerIndividualKeyRaw(...a));

  // ProviderIndividuals are shared, deduped by NPI across all TINs. Names come
  // from the wizard input as a single string per practitioner
  // (`ContractInputPractitioner.name`). The KG validator requires given_name
  // and surname as separate fields, so we split on whitespace: first token is
  // given_name, the remainder joins back into surname. First-seen wins when
  // the same NPI appears at multiple locations.
  const nameByNpi = new Map<string, string>();
  for (const loc of input.locations) {
    for (const p of loc.practitioners) {
      if (p.npi != null && !nameByNpi.has(p.npi)) {
        nameByNpi.set(p.npi, p.name);
      }
    }
  }
  for (const [npi, r] of Object.entries(
    state.resolutions.providerIndividuals,
  )) {
    if (r.status !== 'new') continue;
    const tokens = (nameByNpi.get(npi) ?? '').trim().split(/\s+/);
    const givenName = tokens[0] ?? '';
    const surname = tokens.slice(1).join(' ');
    plan.push({
      step: `createProviderIndividual:${npi}`,
      description: `Create ProviderIndividual NPI ${npi}`,
      idempotencyKey: providerIndividualKey(npi),
      isWrite: true,
      execute: c =>
        c.createProviderIndividual({
          providerIndividual: { givenName, surname, npi1: npi },
          idempotencyKey: providerIndividualKey(npi),
          sourceTracking: {
            ...manualSourceTracking(),
            updateMask: { fields: ['given_name', 'surname', 'npi_1'] },
          },
        }),
    });
  }

  // One fee schedule is created/reused once and shared across all TIN
  // contracts; each per-TIN contract below links to it via
  // addFeeScheduleToContract.
  const fs = state.decisions.feeSchedule;
  const fsAnchor = `${fs?.name ?? ''}:${fs?.effectiveStart ?? ''}`;
  if (fs != null) {
    if (fs.reuseId == null) {
      const lineCount = fs.lines.length;
      plan.push({
        step: 'createFeeSchedule',
        description: `Create FeeSchedule (DRAFT): ${fs.name} · ${lineCount} line${lineCount === 1 ? '' : 's'}`,
        idempotencyKey: feeScheduleKey(fs.name, fs.effectiveStart),
        isWrite: true,
        execute: async (c, _s, runtime) => {
          const response = await c.createFeeSchedule({
            feeSchedule: {
              name: fs.name,
              state: FeeScheduleState.DRAFT,
              effectiveStart: isoToProtoDate(fs.effectiveStart),
              effectiveEnd: isoToProtoDate(fs.effectiveEnd),
              services: fs.lines.map(line => ({
                kgEntityType:
                  line.kind === 'bundle'
                    ? ServiceType.BUNDLE
                    : ServiceType.ITEM,
                kgEntityId: line.refId,
                prices: [
                  {
                    amount: dollarsToMoney(line.priceDollars),
                    effectiveStart: isoToProtoDate(fs.effectiveStart),
                    effectiveEnd: isoToProtoDate(fs.effectiveEnd),
                  },
                ],
              })),
            },
            idempotencyKey: feeScheduleKey(fs.name, fs.effectiveStart),
          });
          runtime.feeScheduleId =
            response.feeSchedule.id ?? runtime.feeScheduleId;
          return response;
        },
      });
    }
    // Provider Network refuses to attach a DRAFT fee schedule to a contract,
    // so we must guarantee EXECUTED state before addFeeScheduleToContract.
    // Runs for both fresh-create (always DRAFT after createFeeSchedule) and
    // reuse (could already be EXECUTED). Read-before-write so we don't try
    // to re-transition a schedule that's already there — the BFF rejects
    // no-op state transitions.
    plan.push({
      step: 'updateFeeScheduleState',
      description: 'Ensure FeeSchedule state is EXECUTED',
      idempotencyKey: feeScheduleStateKey(fsAnchor, FeeScheduleState.EXECUTED),
      isWrite: true,
      execute: async (c, _s, runtime) => {
        // If feeScheduleId is empty (malformed state) we let getFeeSchedule
        // reject — surfacing as a logged error that routes through resume —
        // rather than returning a silent success that would advance the user
        // to the Done page despite the transition never running.
        const feeScheduleId = fs.reuseId ?? runtime.feeScheduleId ?? '';
        const current = await c.getFeeSchedule({ id: feeScheduleId });
        if (current.feeSchedule.state === FeeScheduleState.EXECUTED) {
          return { alreadyExecuted: true, feeScheduleId };
        }
        return c.updateFeeScheduleState({
          feeScheduleId,
          state: FeeScheduleState.EXECUTED,
          idempotencyKey: feeScheduleStateKey(
            fsAnchor,
            FeeScheduleState.EXECUTED,
          ),
        });
      },
    });
  }

  // Each distinct tax_id produces one Merchant + one Contract referencing the
  // shared fee schedule above. Step names are TIN-suffixed for multi-org runs
  // so the review tree's per-TIN nodes resolve commit-log status independently;
  // single-org runs omit the suffix.
  const inputSel = state.decisions.locationSelection.input;
  for (const group of groups) {
    const tin = group.tax_id;
    const legalName = state.decisions.orgNames[tin] ?? input.entity_legal_name;

    const merchantRes = state.resolutions.merchant[tin];
    const merchantStep = isMultiOrg
      ? `createMerchant:${tin}`
      : 'createMerchant';
    if (merchantRes?.status === 'new') {
      plan.push({
        step: merchantStep,
        description: `Create Merchant: ${legalName}`,
        idempotencyKey: merchantKey(tin),
        isWrite: true,
        execute: async (c, _s, runtime) => {
          const response = await c.createMerchant({
            merchant: {
              name: legalName,
              legalName,
              tin,
            },
            idempotencyKey: merchantKey(tin),
            sourceTracking: {
              ...manualSourceTracking(),
              updateMask: { fields: ['name', 'legal_name', 'tin'] },
            },
          });
          const newId = response.merchant.id;
          if (newId != null) runtime.merchantIds[tin] = newId;
          return response;
        },
      });
    } else {
      plan.push({
        step: isMultiOrg ? `reuseMerchant:${tin}` : 'reuseMerchant',
        description: `Reuse Merchant: ${legalName}`,
        idempotencyKey: merchantKey(tin),
        isWrite: false,
      });
    }

    // Operator-driven selection model: only input rows the operator left
    // checked on the Locations step contribute create/link steps. Unchecked
    // rows are skipped entirely. Each link step references this TIN's
    // runtime.merchantIds[tin] so multi-org locations are never linked to the
    // wrong Merchant.
    for (const loc of group.locations) {
      const key = `${loc.address.locality}|${loc.address.address_line_1}`;
      if (inputSel[key] === false) continue;
      const r = state.resolutions.merchantLocations[key];
      const npi2 = loc.group_npi_type2;
      if (r?.status === 'new') {
        const isDev = process.env.NODE_ENV === 'development';
        plan.push({
          step: `createMerchantLocation:${key}`,
          description: `Create MerchantLocation: ${state.decisions.locationNames[key] ?? loc.address.locality}`,
          idempotencyKey: merchantLocationKey(tin, npi2),
          isWrite: true,
          execute: async (c, _s, runtime) => {
            const response = await c.createMerchantLocation({
              merchantLocation: {
                name:
                  state.decisions.locationNames[key] ?? loc.address.locality,
                displayName:
                  state.decisions.locationNames[key] ?? loc.address.locality,
                ...(isDev
                  ? { isVirtual: true }
                  : { address: addressInputToProto(loc.address) }),
                tin,
                npi2,
                phoneNumber: phoneToE164(loc.phone),
                faxNumber: phoneToE164(loc.fax),
              },
              idempotencyKey: merchantLocationKey(tin, npi2),
              sourceTracking: {
                ...manualSourceTracking(),
                updateMask: {
                  fields: [
                    'name',
                    'display_name',
                    isDev ? 'is_virtual' : 'address',
                    'tin',
                    'npi_2',
                    'phone_number',
                    'fax_number',
                  ],
                },
              },
            });
            const newId = response.merchantLocation.id;
            if (newId != null) runtime.merchantLocationIds[key] = newId;
            return response;
          },
        });
      }

      // Link step. Always emit for checked input rows — `new` rows must link
      // the freshly-minted ML, and `existing` rows came from a TIN+NPI2
      // point-lookup that proves the ML exists in KG but doesn't prove it's
      // linked to the Merchant we resolved by TIN. The upstream RPC is
      // idempotent so a re-link of an already-linked pair is a safe no-op.
      // Pure-existing-table rows (operator-checked from the bottom table) skip
      // this loop entirely; they are already linked by construction (only
      // linked locations come back from listMerchantLocationsForMerchant) and
      // flow downstream via CSPs.
      const needsLink = r?.status === 'new' || r?.status === 'existing';
      if (needsLink) {
        plan.push({
          step: `linkMerchantLocation:${loc.address.locality}`,
          description: `Link MerchantLocation → Merchant: ${loc.address.locality}`,
          idempotencyKey: linkMerchantLocationKey(`<ml:${key}>`, `<m:${tin}>`),
          isWrite: true,
          execute: async (c, s, runtime) => {
            const mlId =
              runtime.merchantLocationIds[key] ??
              s.resolutions.merchantLocations[key]?.data?.merchantLocationId ??
              '';
            const mId =
              runtime.merchantIds[tin] ??
              s.resolutions.merchant[tin]?.data?.merchantId ??
              '';
            return c.linkMerchantLocationToMerchant({
              merchantLocationId: mlId,
              merchantId: mId,
              idempotencyKey: linkMerchantLocationKey(mlId, mId),
              sourceTracking: manualSourceTracking(),
            });
          },
        });
      }
    }

    if (state.decisions.createAccessPoints) {
      for (const loc of group.locations) {
        const lk = `${loc.address.locality}|${loc.address.address_line_1}`;
        // Deselected location ⇒ no MerchantLocation to bind to; skip its
        // AccessPoints (same gate as the MerchantLocation loop above).
        if (inputSel[lk] === false) continue;
        for (const p of loc.practitioners) {
          if (p.npi == null) continue;
          const npi = p.npi;
          // mlId for the idempotency key falls back to the placeholder used
          // before commit; the real id is read from `runtime` at execute time.
          const placeholderMlId =
            state.resolutions.merchantLocations[lk]?.data?.merchantLocationId ??
            `<ml:${lk}>`;
          const piId =
            state.resolutions.providerIndividuals[npi]?.data
              ?.providerIndividualId ?? '';
          plan.push({
            step: `createAccessPoint:${loc.address.locality}:${npi}`,
            description: `Create AccessPoint: ${p.name} @ ${loc.address.locality}`,
            idempotencyKey: accessPointKey(placeholderMlId, piId),
            isWrite: true,
            execute: async (c, _s, runtime) => {
              const mlId = runtime.merchantLocationIds[lk] ?? placeholderMlId;
              return c.createAccessPoint({
                accessPoint: {},
                merchantLocationId: mlId,
                providerIndividualId: piId,
                idempotencyKey: accessPointKey(mlId, piId),
                sourceTracking: manualSourceTracking(),
              });
            },
          });
        }
      }
    }

    // ProviderAccount is the money-side wrapper around a legal-entity
    // Organization. Each TIN has its own CA decision (create vs reuse).
    const ca = state.decisions.customerAccount[tin];
    const orgStep = isMultiOrg
      ? `ensureOrganization:${tin}`
      : 'ensureOrganization';
    const paStep = isMultiOrg
      ? `createProviderAccount:${tin}`
      : 'createProviderAccount';
    if (ca?.mode === 'create') {
      plan.push({
        step: orgStep,
        description: `Ensure Organization: ${ca.name}`,
        idempotencyKey: organizationKey(ca.taxId),
        isWrite: true,
        execute: async (c, _s, runtime) => {
          // Reuse-then-create: look up by FEIN first so we don't double-create
          // an Organization that some other flow already inserted. Normalize
          // the FEIN because the proto's CreateOrganization validates
          // ^[0-9]{9}$ but the lookup field doesn't.
          const fein = normalizeFein(ca.taxId);
          const lookup = await c.getOrganization({
            organizationIdentifier: { organizationFein: fein },
          });
          if (lookup.ok && lookup.data.organization.organizationId) {
            runtime.organizationIds[tin] =
              lookup.data.organization.organizationId;
            return { reused: true, organization: lookup.data.organization };
          }
          const response = await c.createOrganization({
            organization: {
              legalName: ca.name,
              fein,
              corpType: CorpType.OTHER_BUSINESS_TYPE,
              state: OrganizationState.ACTIVE,
            },
            idempotencyKey: organizationKey(ca.taxId),
          });
          const newId = response.organization.organizationId;
          if (newId != null) runtime.organizationIds[tin] = newId;
          return response;
        },
      });
      plan.push({
        step: paStep,
        description: `Create ProviderAccount: ${ca.name}`,
        idempotencyKey: providerAccountKey(ca.taxId),
        isWrite: true,
        execute: async (c, _s, runtime) => {
          const response = await c.createProviderAccount({
            providerAccount: {
              organizationId: runtime.organizationIds[tin],
            },
            idempotencyKey: providerAccountKey(ca.taxId),
          });
          const newId = response.providerAccount.providerAccountId;
          if (newId != null) runtime.providerAccountIds[tin] = newId;
          return response;
        },
      });
    } else if (ca?.mode === 'reuse') {
      plan.push({
        step: isMultiOrg
          ? `reuseProviderAccount:${tin}`
          : 'reuseProviderAccount',
        description: `Reuse ProviderAccount: ${ca.name}`,
        idempotencyKey: providerAccountKey(tin),
        isWrite: false,
      });
    }

    const contract = state.decisions.contract[tin];
    if (contract != null) {
      // The body's providerCustomerAccountId must be a real UUID. For reuse,
      // we have it at plan-build time; for create, it's only known after
      // createProviderAccount runs (or, on resume, recoverable via FEIN).
      // The idempotency key uses a stable plan-time anchor (account id when
      // reusing, FEIN when creating) so retries dedupe without baking a
      // placeholder into the key namespace.
      const idempotencyAnchor =
        ca?.mode === 'reuse' ? ca.id : (ca?.taxId ?? '');
      const contractStep = isMultiOrg
        ? `createContract:${tin}`
        : 'createContract';
      if (contract.mode === 'create') {
        plan.push({
          step: contractStep,
          description: `Create Contract (DRAFT): ${contract.name}`,
          idempotencyKey: contractKey(
            idempotencyAnchor,
            contract.effectiveStart,
          ),
          isWrite: true,
          execute: async (c, _s, runtime) => {
            let providerCustomerAccountId: string;
            if (ca?.mode === 'reuse') {
              providerCustomerAccountId = ca.id;
            } else if (runtime.providerAccountIds[tin] != null) {
              providerCustomerAccountId = runtime.providerAccountIds[tin];
            } else if (ca?.mode === 'create') {
              const lookup = await c.getProviderAccount({
                providerAccountIdentifier: {
                  organizationFein: normalizeFein(ca.taxId),
                },
              });
              providerCustomerAccountId = lookup.ok
                ? (lookup.data.providerAccount.providerAccountId ?? '')
                : '';
              if (providerCustomerAccountId !== '') {
                runtime.providerAccountIds[tin] = providerCustomerAccountId;
              }
            } else {
              providerCustomerAccountId = '';
            }
            const response = await c.createContract({
              contract: {
                name: contract.name,
                providerCustomerAccountId,
                state: ContractState.DRAFT,
                effectiveStart: isoToProtoDate(contract.effectiveStart),
                effectiveEnd:
                  contract.openEnded || !contract.effectiveEnd
                    ? undefined
                    : isoToProtoDate(contract.effectiveEnd),
              },
              idempotencyKey: contractKey(
                idempotencyAnchor,
                contract.effectiveStart,
              ),
            });
            const newId = response.contract.id;
            if (newId != null) runtime.contractIds[tin] = newId;
            return response;
          },
        });
      } else {
        // Reuse path: surface the decision in the plan for review-step
        // visibility, but don't write. runtime.contractIds[tin] is seeded at
        // runFrom start so downstream addFeeScheduleToContract /
        // addContractServiceProviders can read it without a createContract
        // response.
        plan.push({
          step: isMultiOrg ? `reuseContract:${tin}` : 'reuseContract',
          description: `Reuse Contract: ${contract.existingName}`,
          idempotencyKey: contractKey(
            idempotencyAnchor,
            contract.effectiveStart,
          ),
          isWrite: false,
        });
      }

      // Stable plan-time anchors so the displayed idempotency key matches the
      // one sent at execute time, even though the real UUIDs aren't known
      // until createContract / createFeeSchedule run.
      const contractAnchor = `${idempotencyAnchor}:${contract.effectiveStart}`;
      const linkFsStep = isMultiOrg
        ? `addFeeScheduleToContract:${tin}`
        : 'addFeeScheduleToContract';
      plan.push({
        step: linkFsStep,
        description: 'Link FeeSchedule → Contract',
        idempotencyKey: linkFeeScheduleToContractKey(contractAnchor, fsAnchor),
        isWrite: true,
        execute: async (c, _s, runtime) => {
          const contractId = runtime.contractIds[tin] ?? '';
          const feeScheduleId = fs?.reuseId ?? runtime.feeScheduleId ?? '';
          // Always check linkage before posting. `listFeeSchedules` filtered
          // by contractId returns fee schedules already attached — if ours is
          // in the list the link exists and re-posting would 400.
          if (contractId !== '' && feeScheduleId !== '') {
            const linked = await c.listFeeSchedules({
              contractId,
              paginationParams: { pageSize: 20 },
            });
            const alreadyLinked = (linked.feeSchedules ?? []).some(
              f => f.id === feeScheduleId,
            );
            if (alreadyLinked) {
              return { alreadyLinked: true, contractId, feeScheduleId };
            }
          }
          return c.addFeeScheduleToContract({
            contractId,
            feeScheduleId,
            idempotencyKey: linkFeeScheduleToContractKey(
              contractAnchor,
              fsAnchor,
            ),
          });
        },
      });

      // CSPs filtered to this TIN's locations. Build the set of row IDs that
      // belong to this TIN: input keys (`${locality}|${address}`) come from
      // group.locations; existing-table keys (`existing:${mlId}`) come from
      // state.resolutions.existingMerchantLocations[tin].
      const csps = state.decisions.csps;
      if (csps != null) {
        const tinLocKeys = new Set<string>();
        for (const loc of group.locations) {
          tinLocKeys.add(
            `${loc.address.locality}|${loc.address.address_line_1}`,
          );
        }
        for (const ml of state.resolutions.existingMerchantLocations[tin] ??
          []) {
          if (ml.id != null) tinLocKeys.add(`existing:${ml.id}`);
        }
        const tinRows = csps.rows.filter(r => tinLocKeys.has(r.id));
        const includedCount = tinRows.filter(r => r.included).length;
        if (includedCount > 0) {
          const cspStep = isMultiOrg
            ? `addContractServiceProviders:${tin}`
            : 'addContractServiceProviders';
          plan.push({
            step: cspStep,
            description: `Add ${includedCount} ContractServiceProvider${includedCount === 1 ? '' : 's'} (bulk)`,
            idempotencyKey: cspBulkKey(contractAnchor, contract.effectiveStart),
            isWrite: true,
            execute: async (c, s, runtime) => {
              const contractId = runtime.contractIds[tin] ?? '';
              const feeScheduleId = fs?.reuseId ?? runtime.feeScheduleId ?? '';
              // CSP row ids are dual-namespace in location mode: input-row
              // keys (`${locality}|${address}`) resolve through
              // runtime.merchantLocationIds (for `new` rows minted this run)
              // or state.resolutions (for `existing` rows resolved by
              // point-lookup); existing-table-only rows carry an `existing:`
              // sentinel prefix and pass through with no lookup since the
              // suffix is the merchantLocationId itself.
              const resolveLocationId = (rowId: string): string => {
                if (rowId.startsWith('existing:'))
                  return rowId.slice('existing:'.length);
                return (
                  runtime.merchantLocationIds[rowId] ??
                  s.resolutions.merchantLocations[rowId]?.data
                    ?.merchantLocationId ??
                  ''
                );
              };
              const entries = tinRows
                .filter(r => r.included)
                .map(r => ({
                  feeScheduleId,
                  knowledgeEntityId:
                    csps.mode === 'access_point'
                      ? (s.resolutions.accessPoints[r.id]?.data
                          ?.accessPointId ?? '')
                      : resolveLocationId(r.id),
                  knowledgeEntityType:
                    csps.mode === 'access_point'
                      ? ServiceProviderType.ACCESS_POINT
                      : ServiceProviderType.MERCHANT_LOCATION,
                  effectiveStart: isoToProtoDate(r.effectiveStart),
                  effectiveEnd: r.effectiveEnd
                    ? isoToProtoDate(r.effectiveEnd)
                    : undefined,
                }));
              // Multiple input locations can resolve to the same
              // MerchantLocation in the KG; the server rejects overlapping
              // rows for one entity. In location mode collapse to first
              // occurrence per knowledgeEntityId.
              const dedupedEntries =
                csps.mode === 'location'
                  ? entries.filter(
                      (e, i, arr) =>
                        arr.findIndex(
                          x => x.knowledgeEntityId === e.knowledgeEntityId,
                        ) === i,
                    )
                  : entries;
              // Read-before-write: drop rows the server already has attached
              // for this contract. Mirrors the addFeeScheduleToContract guard
              // above — covers prior-session attachments and partial
              // commit-log loss that the idempotency key alone can't catch.
              let contractServiceProviders = dedupedEntries;
              if (contractId !== '') {
                const existing = await c.getContractServiceProviders({
                  contractId,
                });
                const existingKeys = new Set(
                  (existing.contractServiceProviders ?? []).map(
                    csp =>
                      `${csp.feeScheduleId}|${csp.knowledgeEntityType}|${csp.knowledgeEntityId}`,
                  ),
                );
                contractServiceProviders = dedupedEntries.filter(
                  e =>
                    !existingKeys.has(
                      `${e.feeScheduleId}|${e.knowledgeEntityType}|${e.knowledgeEntityId}`,
                    ),
                );
                if (contractServiceProviders.length === 0) {
                  return {
                    alreadyLinked: true,
                    contractId,
                    skipped: dedupedEntries.length,
                  };
                }
              }
              return c.addContractServiceProviders({
                contractId,
                contractServiceProviders,
                idempotencyKey: cspBulkKey(
                  contractAnchor,
                  contract.effectiveStart,
                ),
              });
            },
          });
        }
      }

      // Contract state transition (last step per TIN). Must run after CSPs /
      // fee-schedule attachments so the contract isn't flipped to EXECUTED
      // while it's still being assembled. Runs for both create (always DRAFT
      // after createContract) and reuse (may be DRAFT or EXECUTED).
      // Read-before-write covers both without a redundant transition. Version
      // is read at execute time because reused contracts can be at any version.
      const updateContractStep = isMultiOrg
        ? `updateContract:${tin}`
        : 'updateContract';
      plan.push({
        step: updateContractStep,
        description: 'Ensure Contract state is EXECUTED',
        idempotencyKey: contractStateKey(
          contractAnchor,
          ContractState.EXECUTED,
          1,
        ),
        isWrite: true,
        execute: async (c, _s, runtime) => {
          const contractId = runtime.contractIds[tin] ?? '';
          const current = await c.getContract({ id: contractId });
          if (current.contract.state === ContractState.EXECUTED) {
            return { alreadyExecuted: true, contractId };
          }
          const version = current.contract.version ?? 1;
          return c.updateContract({
            contractId,
            state: ContractState.EXECUTED,
            version,
            idempotencyKey: contractStateKey(
              contractAnchor,
              ContractState.EXECUTED,
              version,
            ),
          });
        },
      });
    }
  }

  return plan;
};

export interface OrchestratorState {
  plan: PlannedAction[];
  log: CommitLogEntry[];
  running: boolean;
  done: boolean;
  error: string | null;
  resumeFromStep: string | null;
}

export interface UseCommitOrchestrator {
  state: OrchestratorState;
  commit: () => Promise<void>;
  resume: () => Promise<void>;
}

export const useCommitOrchestrator = (): UseCommitOrchestrator => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const [running, setRunning] = useState(false);

  const plan = useMemo(
    () =>
      buildPlan(
        state,
        state.commit.resumeAttempt ?? 0,
        state.commit.resumeNonce ?? '',
      ),
    [state],
  );

  // Short random suffix for each retry pass. Generated once per Resume click
  // and persisted in commit state so every step in a single retry pass shares
  // the same nonce — otherwise a step that succeeded and a later step that
  // failed in the same pass would have used different keys.
  const newResumeNonce = (): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);

  const recordLog = useCallback(
    (entry: CommitLogEntry): void => {
      setState(s => ({
        ...s,
        commit: { ...s.commit, log: [...s.commit.log, entry] },
      }));
    },
    [setState],
  );

  const runFrom = useCallback(
    async (
      startStep: string | null,
      attempt: number,
      nonce: string,
    ): Promise<void> => {
      setRunning(true);
      setState(s => ({
        ...s,
        commit: {
          ...s.commit,
          started: true,
          completed: false,
          resumeFromStep: null,
          resumeAttempt: attempt,
          resumeNonce: nonce,
        },
      }));
      // Build a fresh plan for this run so the new attempt counter and nonce
      // are reflected in idempotency keys; the memoized `plan` may still hold
      // the previous pass's keys until React applies the state update.
      const runPlan = buildPlan(state, attempt, nonce);
      const runGroups = state.input != null ? getOrgGroups(state.input) : [];
      const seed = seedRuntimeFromLog(state.commit.log, state.input);
      // Pre-seed merchant IDs from resolutions for reused (existing) merchants.
      const merchantIdsInit: Record<string, string> = { ...seed.merchantIds };
      for (const g of runGroups) {
        if (merchantIdsInit[g.tax_id] == null) {
          const mId = state.resolutions.merchant[g.tax_id]?.data?.merchantId;
          if (mId != null) merchantIdsInit[g.tax_id] = mId;
        }
      }
      // Pre-seed contract IDs from decisions for reused contracts so
      // addFeeScheduleToContract / addContractServiceProviders can read them
      // without a prior createContract response.
      const contractIdsInit: Record<string, string> = { ...seed.contractIds };
      for (const g of runGroups) {
        if (contractIdsInit[g.tax_id] == null) {
          const cd = state.decisions.contract[g.tax_id];
          if (cd?.mode === 'reuse') contractIdsInit[g.tax_id] = cd.existingId;
        }
      }
      const runtime: RuntimeRegistry = {
        merchantIds: merchantIdsInit,
        merchantLocationIds: { ...seed.merchantLocationIds },
        organizationIds: { ...seed.organizationIds },
        providerAccountIds: { ...seed.providerAccountIds },
        contractIds: contractIdsInit,
        feeScheduleId: seed.feeScheduleId,
      };
      const simulateOnly = state.commit.simulateOnly;
      let started = startStep == null;
      for (const action of runPlan) {
        if (!started) {
          if (action.step !== startStep) continue;
          started = true;
        }
        if (!action.isWrite) {
          recordLog({
            step: action.step,
            status: 'success',
            idempotencyKey: action.idempotencyKey,
            response: 'reuse',
          });
          continue;
        }
        // Simulate mode: walk the plan but never call the BFF. Write rows are
        // logged with status 'simulated' so the UI distinguishes them from
        // real successes; runtime registry stays empty, so id-dependent
        // descriptions like idempotency keys still surface their plan-time
        // anchors. completed stays false at run end so the operator can flip
        // to Live and re-Commit without resetting the wizard.
        if (simulateOnly) {
          recordLog({
            step: action.step,
            status: 'simulated',
            idempotencyKey: action.idempotencyKey,
            response: { simulated: true, note: 'Simulate mode: no write sent' },
          });
          continue;
        }
        if (action.execute == null) continue;
        try {
          const response = await action.execute(client, state, runtime);
          recordLog({
            step: action.step,
            status: 'success',
            idempotencyKey: action.idempotencyKey,
            response,
          });
        } catch (e) {
          const err = (e as Error).message;
          recordLog({
            step: action.step,
            status: 'error',
            idempotencyKey: action.idempotencyKey,
            error: err,
          });
          setState(s => ({
            ...s,
            commit: { ...s.commit, resumeFromStep: action.step },
          }));
          setRunning(false);
          return;
        }
      }
      setState(s => ({
        ...s,
        commit: {
          ...s.commit,
          completed: !simulateOnly,
          resumeFromStep: null,
        },
      }));
      setRunning(false);
    },
    [client, recordLog, setState, state],
  );

  const commit = useCallback(() => runFrom(null, 0, ''), [runFrom]);
  const resume = useCallback(
    () =>
      runFrom(
        state.commit.resumeFromStep,
        (state.commit.resumeAttempt ?? 0) + 1,
        newResumeNonce(),
      ),
    [runFrom, state.commit.resumeFromStep, state.commit.resumeAttempt],
  );

  return {
    state: {
      plan,
      log: state.commit.log,
      running,
      done: state.commit.completed,
      error: null,
      resumeFromStep: state.commit.resumeFromStep,
    },
    commit,
    resume,
  };
};
